import { Injectable } from '@angular/core';
import { environment } from '../../../../environments/environment';
import { Observable } from 'rxjs';
import { HttpClient, HttpParams } from '@angular/common/http';
import { ResponseModel } from '@modules/auth/_models/response.model';
import { NotificationsViewModel } from '@modules/post/_models/notifications-view.model';
import { PostViewModel } from '@modules/post/_models/post-view.model';
import { Reply } from '@modules/post/_models/post-reply-view.model';
import { PagedResponseModel } from '@models/Generic/paged-response.model';
import { IGroupModel } from '@modules/group/models/IGroupModel';
import { PostResponseDataModel } from 'src/app/_models/Post/post-response-data.model';
import { Question } from 'src/app/_models/Post/question.model';
import { Audience } from '@models/Post/audience.model';

const API_USERS_URL = `${environment.apiUrl}`;

export interface PostCreationParams {
  postsPicture: string;
  postsPictureFileName: string;
  postsVideo: string;
  postsVideoFileName: string;
  postText: string;
  audience: number;
  sharpenReference?: string
}

@Injectable({
  providedIn: 'root',
})
export class HttpDashboardService {
  constructor(private http: HttpClient) {
  }

  markAsReadNotification(notificationId: number): Observable<any> {
    const params = new HttpParams().set('notificationId', notificationId.toString());
    return this.http.get<ResponseModel<boolean>>(`${API_USERS_URL}/v1/Notification/mark-read`, {params});
  }

  getFeedPosts(myFeed: boolean, pageNumber: number, pageSize: number): Observable<PagedResponseModel<PostResponseDataModel>> {
    const params = new HttpParams()
      .set('myFeed', myFeed ? 'true' : 'false')
      .set('pageNumber', pageNumber.toString())
      .set('pageSize', pageSize.toString());
    return this.http.get<PagedResponseModel<PostResponseDataModel>>(`${API_USERS_URL}/v1/Post/get-posts`, {params});
  }

  createReply(postId: number, replyText: string, parentReplyId?: number): Observable<ResponseModel<Reply>> {
    return this.http.post<ResponseModel<Reply>>(`${API_USERS_URL}/v1/Post/create-reply`, {replyText, postId, parentReplyId});
  }

  createPost(newPostParams: PostCreationParams): Observable<ResponseModel<any>> {
    const {postText: posts, postsPicture, postsPictureFileName, postsVideo, postsVideoFileName, audience, sharpenReference} = newPostParams;

    const postParams = {
      isPost: true,
      posts,
      postsPicture,
      postsPictureFileName,
      postsBase64Video: postsVideo,
      videoFileName: postsVideoFileName,
      audience,
      sharpenReference,
    };
    return this.http.post<ResponseModel<any>>(`${API_USERS_URL}/v1/Post/create-post`, postParams);
  }

  createQuestion(posts: string, audience: Audience): Observable<ResponseModel<Question>> {
    return this.http.post<ResponseModel<Question>>(`${API_USERS_URL}/v1/Post/create-post`, {
      isPost: false,
      posts,
      audience,
    });
  }

  clearAllNotifications(): Observable<ResponseModel<boolean>> {
    return this.http.get<ResponseModel<boolean>>(`${API_USERS_URL}/v1/Notification/clear-notifications`);
  }

  getAllNotifications(): Observable<Array<NotificationsViewModel>> {
    return this.http.get<Array<NotificationsViewModel>>(`${API_USERS_URL}/v1/Notification/get-notifications`);
  }

  countNewNotifications(): Observable<ResponseModel<number>> {
    return this.http.get<ResponseModel<number>>(`${API_USERS_URL}/v1/Notification/count-of-new-notifications`);
  }

  getPostById(id: number): Observable<ResponseModel<PostViewModel>> {
    return this.http.get<ResponseModel<PostViewModel>>(`${API_USERS_URL}/v1/Post/get-post-by-id/${id}`);
  }

  getRepliesByPostId(id: number): Observable<ResponseModel<PostViewModel>> {
    return this.http.get<ResponseModel<PostViewModel>>(`${API_USERS_URL}/v1/Post/get-replies-by-id/${id}`);
  }

  getPostImageByPostId(id: number): Observable<ResponseModel<PostViewModel>> {
    return this.http.get<ResponseModel<PostViewModel>>(`${API_USERS_URL}/v1/Post/get-postimage-by-id/${id}`);
  }

  getPostVideoByPostId(id: number): Observable<ResponseModel<PostViewModel>> {
    return this.http.get<ResponseModel<PostViewModel>>(`${API_USERS_URL}/v1/Post/get-postvideo-by-id/${id}`);
  }

  deletePost(Id: number): Observable<ResponseModel<boolean>> {
    return this.http.delete<ResponseModel<boolean>>(`${API_USERS_URL}/v1/Post/delete-post/${Id}`);
  }

  deleteReply(Id: number): Observable<ResponseModel<boolean>> {
    return this.http.delete<ResponseModel<boolean>>(`${API_USERS_URL}/v1/Post/delete-reply/${Id}`);
  }

  getLastActiveGroups(): Observable<Array<IGroupModel>> {
    return this.http.get<Array<IGroupModel>>(`${API_USERS_URL}/v1/Group/get-last-active-groups`);
  }

  getLastCreatedGroups(): Observable<Array<IGroupModel>> {
    return this.http.get<Array<IGroupModel>>(`${API_USERS_URL}/v1/Group/get-last-created-groups`);
  }
}
